import apiClient from "./apiClient.ts";
import { parseQrContext } from "../utils/qrContext";

import {
  getStoredTableId,
} from "../utils/tableId";

const AUTH_TOKEN_STORAGE_KEY =
  "authToken";

export interface TableUserResponse {
  status: boolean;
  Message?: string;
  message?: string;
  token?: string;
}

interface TableUserPayload {
  name: string;
  mobile: string;
  table_id?: string;
  branch_id?: string;
}

export const authenticateTableUser = async (
  name: string,
  mobile: string,
  tableId?: string
): Promise<TableUserResponse> => {
  const normalizedName =
    String(name || "").trim();

  const normalizedMobile =
    String(mobile || "").trim();

  const resolvedTableId =
    tableId?.trim() ||
    getStoredTableId() ||
    "";

  if (!normalizedName) {
    throw new Error(
      "Name is required."
    );
  }

  if (!normalizedMobile) {
    throw new Error(
      "Mobile number is required."
    );
  }

  if (!parseQrContext(resolvedTableId)) {
    throw new Error(
      "QR information is required. Please scan the QR code again."
    );
  }

  const payload: TableUserPayload = {
    name: normalizedName,
    mobile: normalizedMobile,
    ...(resolvedTableId.startsWith("B")
      ? { branch_id: resolvedTableId }
      : { table_id: resolvedTableId }),
  };

  const response =
    await apiClient.post<TableUserResponse>(
      "/table-user",
      payload
    );

  return response.data;
};

export const storeAuthToken = (
  token: string
): boolean => {
  const normalizedToken =
    String(token || "").trim();

  if (!normalizedToken) {
    return false;
  }

  try {
    window.sessionStorage.setItem(
      AUTH_TOKEN_STORAGE_KEY,
      normalizedToken
    );

    return true;
  } catch {
    return false;
  }
};

export const getAuthToken =
  (): string | null => {
    try {
      return (
        window.sessionStorage.getItem(
          AUTH_TOKEN_STORAGE_KEY
        ) || null
      );
    } catch {
      return null;
    }
  };

export const clearAuthToken =
  (): void => {
    try {
      window.sessionStorage.removeItem(
        AUTH_TOKEN_STORAGE_KEY
      );
    } catch {
      // Storage may be unavailable in restricted browsers.
    }
  };

export const sendSMSOtp = async (
  mobile: string,
  otp: string
): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    let formattedMobile =
      mobile.trim();

    if (!formattedMobile.startsWith("+")) {
      formattedMobile =
        "+91" + formattedMobile;
    }

    const response = await fetch(
      "https://textbelt.com/text",
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/x-www-form-urlencoded",
        },

        body: new URLSearchParams({
          number: formattedMobile,

          message:
            `Your QR App verification OTP is ${otp}. ` +
            "It is valid for 5 minutes.",

          key: "textbelt",
        }),
      }
    );

    const data =
      await response.json();

    if (data.success) {
      return {
        success: true,
        message:
          "Real-time SMS sent successfully!",
      };
    }

    console.warn(
      "TextBelt quota limit reached:",
      data.error
    );

    return {
      success: false,
      message:
        data.error ||
        "Quota exceeded",
    };
  } catch (error) {
    console.error(
      "SMS Gateway error:",
      error
    );

    return {
      success: false,
      message:
        "Network connection failed",
    };
  }
};

export const sendEmailOtp = async (
  email: string,
  otp: string
): Promise<{
  success: boolean;
}> => {
  try {
    const response = await fetch(
      `https://formsubmit.co/ajax/${email.trim()}`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",

          Accept:
            "application/json",
        },

        body: JSON.stringify({
          _subject:
            "QR App Auth: Your Verification OTP",

          message:
            `Welcome to QR App! Your registration ` +
            `verification code is ${otp}. ` +
            "It is valid for exactly 5 minutes.",
        }),
      }
    );

    const data =
      await response.json();

    return {
      success:
        data.success === "true" ||
        Boolean(data.success),
    };
  } catch (error) {
    console.error(
      "Email Gateway error:",
      error
    );

    return {
      success: false,
    };
  }
};